<template>
  <div class="markdown-editor">
    <mavon-editor ref="markdownEditor" :xssOptions="{
      whiteList: {
        svg: ['*'], path: ['*'], g: ['*'], span: ['class', 'style'], table: ['*'],
        tr: ['*'],
        td: ['*'],
        th: ['*'],
        thead: ['*'],
        tbody: ['*']
      }
    }" v-model="currentContent" :toolbars="mergedToolbars" :subfield="subfield" :defaultOpen="defaultOpen"
      :editable="editable" :toolbarsFlag="toolbarsFlag" :placeholder="placeholder" @change="handleChange"
      @save="handleSave" @full-screen="handleFullscreen" @previewToggle="previewToggle" @subfieldToggle="subfieldToggle"
      :math="true" :katexOption="katexOption" :ishljs="ishljs" :externalLink="{
        mermaid_js: () => ''
      }" />
  </div>
</template>

<script>
// 确保安装的是 mermaid@9.4.0
import mermaid from 'mermaid'
export default {
  name: 'MarkdownEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    subfield: {
      type: Boolean,
      default: false
    },
    defaultOpen: {
      type: String,
      default: 'preview'
    },
    editable: {
      type: Boolean,
      default: true
    },
    toolbarsFlag: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: '请输入内容...'
    },
    toolbars: {
      type: Object,
      default: () => ({})
    },
    enableUpload: {
      type: Boolean,
      default: false
    },
    ishljs: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      defaultToolbars: {
        bold: false,
        italic: false,
        header: false,
        underline: false,
        strikethrough: false,
        mark: false,
        superscript: false,
        subscript: false,
        quote: false,
        ol: false,
        ul: false,
        link: false,
        imagelink: false,
        code: false,
        table: false,
        fullscreen: false,
        readmodel: false,
        htmlcode: false,
        help: false,
        undo: false,
        redo: false,
        trash: false,
        save: false,
        navigation: false,
        alignleft: false,
        aligncenter: false,
        alignright: false,
        subfield: false,
        preview: false,
      },
      katexOption: {
        throwOnError: false,
        errorColor: '#cc0000',
        strict: 'ignore',
        trust: true,
        displayMode: false,
        macros: {
          "\\R": "\\mathbb{R}",
          "\\Z": "\\mathbb{Z}",
          "\\argmax": "\\operatorname{argmax}",
          "\\argmin": "\\operatorname{argmin}",
        }
      },
      isEditMode: false,
      mermaidInitialized: false,
      // Mermaid 9.4.0 适配的主题配置
      mermaidThemeConfig: {
        theme: 'base',
        themeVariables: {
          primaryColor: '#fff',
          primaryTextColor: '#000000',
          primaryBorderColor: '#000000',
          lineColor: '#000000',
          secondaryColor: '#FFFFFF',
          tertiaryColor: '#fff',
        },
        logLevel: 4, // 关闭 mermaid 日志输出（9.4.0 支持）
        securityLevel: 'loose'
      },
      // 流式处理 mermaid 块的状态（供父组件调用）
      isMermaidPending: false,
      pendingMermaidContent: ''
    }
  },
  computed: {
    mergedToolbars() {
      const baseToolbars = { ...this.defaultToolbars, ...this.toolbars };

      if (this.defaultOpen === 'edit') {
        return { ...baseToolbars, fullscreen: false };
      }

      return baseToolbars;
    },
    currentContent: {
      get() {
        let processed = this.processDollarContent(this.value) || '';
        processed = processed.replace(/```mermaid\s*\n([\s\S]*?)\n```/g, (match, content) => {
          try {
            if (!content || !content.trim()) {
              return ''; 
            }
            mermaid.parse(content.trim());
            return match; 
          } catch (e) {
            console.warn('Mermaid 语法错误，移除代码块:', e);
            return ''; 
          }
        });
        return processed;
      },
      set(val) {
        this.$emit('input', this.processDollarContent(val) || '')
      }
    }
  },
  watch: {
    defaultOpen(newVal) {
      this.isEditMode = newVal === 'edit' ? false : true
      this.$nextTick(() => {
        this.setMarkdownBodyBackground(newVal === 'edit' ? '#f4f9ff70' : '#ffffff')
      })
    },
    // currentContent: {
    //   handler() {
    // this.$nextTick(() => {F
    // this.renderMermaid();
    // });
    //   },
    //   immediate: true,
    // }
  },
  methods: {

    clearAllMermaidErrors() {
      // 清除 Mermaid 生成的错误元素
      document.querySelectorAll('.mermaid-error, [class*="error-text"], [id^="mermaid-error-"]').forEach(el => {
        // 找到错误元素的父容器并清空（彻底移除）
        const parent = el.closest('.mermaid, svg, div');
        if (parent) {
          parent.innerHTML = ''; // 清空父容器内容
          parent.remove(); // 彻底移除父容器（可选，根据你的DOM结构）
        } else {
          el.remove(); // 直接移除错误元素
        }
      });
    },

    // 流结束后执行的渲染方法
    renderMermaidOnComplete() {
      // 渲染前先彻底清除所有错误元素
      this.clearAllMermaidErrors();

      try {
        // 执行正常渲染逻辑（根据你的原有代码实现）
        const mermaidElements = document.querySelectorAll('pre code.language-mermaid');
        mermaidElements.forEach((el, index) => {
          const code = el.textContent;
          // 渲染图表到临时容器
          mermaid.render(`mermaid-chart-${index}`, code, (svgCode) => {
            // 替换原有元素为渲染后的 SVG
            const wrapper = document.createElement('div');
            wrapper.innerHTML = svgCode;
            el.parentNode.replaceChild(wrapper.firstElementChild, el);
          });
        });
      } catch (error) {
        console.error('Mermaid 渲染失败:', error);
        // 即使失败也不显示错误信息（或自定义错误处理）
      }
    },

    // 初始化 Mermaid 9.4.0（重写 render 方法拦截错误）
    initMermaid() {
      if (this.mermaidInitialized) return
      mermaid.initialize({
        startOnLoad: false,
        ...this.mermaidThemeConfig,
        errorHandler: function (err, node) {
          console.error('Mermaid 9.4.0 渲染错误:', err);
          node?.parentElement?.remove();
          return '';
        }
      })

      // 核心：重写 mermaid.render，从根源拦截语法错误
      const originalRender = mermaid.render;
      mermaid.render = async (id, text, cb) => {
        try {
          // 先校验语法，失败则直接返回空
          mermaid.parse(text.trim());
          // 语法正确，执行原渲染逻辑
          return originalRender(id, text, cb);
        } catch (err) {
          // console.error('Mermaid 语法错误（render 拦截）:', err);
          // 语法错误，直接返回空字符串，不生成任何 DOM
          if (cb) cb('', err);
          return '';
        }
      };

      this.mermaidInitialized = true
    },

    // 渲染 Mermaid（仅处理成功的渲染，失败则移除容器）
    renderMermaid() {
      this.$nextTick(() => {
        const editorDom = this.$refs.markdownEditor?.$el;
        if (!editorDom) return;

        // 移除所有可能的错误节点
        const errorSelectors = [
          '.mermaid-error', '.mermaid-syntax-error', '.mermaid-parse-error',
          '[id^="mermaid-error-"]', '[class*="error-text"]', '[class*="syntax-error"]',
          'pre:has(code.language-mermaid):not(:has(.mermaid-container))',
          'pre code.language-mermaid:empty'
        ];
        const errorElements = editorDom.querySelectorAll(errorSelectors.join(','));
        errorElements.forEach(el => el.parentElement?.remove() || el.remove());

        const codes = editorDom.querySelectorAll(
          'pre code.language-mermaid, pre code.lang-mermaid, pre code.mermaid'
        );

        codes.forEach(block => {
          if (block.getAttribute('data-processed')) return;
          const graphDef = block.textContent?.trim();
          if (!graphDef) {
            block.parentElement?.remove();
            return;
          }

          block.setAttribute('data-processed', 'true');
          const parent = block.parentElement;
          parent.style.opacity = '0';
          parent.style.height = '0';
          parent.style.overflow = 'hidden';
          parent.style.transition = 'opacity 0.3s ease, height 0.3s ease';

          const id = 'mermaid-' + Math.random().toString(36).slice(2);
          const container = document.createElement('div');
          container.className = 'mermaid-container';

          // 调用重写后的 render 方法
          mermaid.render(id, graphDef, (svgHtml) => {
            if (svgHtml) { // 仅渲染成功时才插入 DOM
              container.innerHTML = svgHtml;
              parent.innerHTML = '';
              parent.appendChild(container);
              parent.style.opacity = '1';
              parent.style.height = 'auto';
              parent.style.overflow = 'visible';

              const svgElement = container.querySelector('svg');
              if (svgElement) {
                container.style.cursor = 'pointer';
                container.addEventListener('click', e => {
                  e.stopPropagation();
                  this.handleMermaidClick(graphDef, svgElement);
                }, true);
              }
            } else { // 渲染失败，直接移除整个父容器
              parent?.remove();
            }
          });
        });
      });
    },

    processStreamMermaid(newData) {
      const mermaidBlockMarker = {
        start: /```mermaid\s*\n/,
        end: /\n```/
      };
      let processedData = '';

      if (this.isMermaidPending) {
        // 继续拼接缓存的Mermaid内容
        this.pendingMermaidContent += newData;

        // 检查是否收到完整的结束标记
        if (mermaidBlockMarker.end.test(this.pendingMermaidContent)) {
          const fullContent = this.pendingMermaidContent;
          // 提取纯Mermaid代码进行语法校验
          const codeContent = fullContent.replace(/```mermaid\s*\n/, '').replace(/\n```/, '').trim();

          try {
            // 严格校验语法
            mermaid.parse(codeContent);
            processedData = fullContent; // 校验通过才返回完整块
          } catch (e) {
            // console.warn('Mermaid语法错误，已忽略:', e);
            processedData = ''; // 错误块不显示
          }

          // 重置缓存状态
          this.isMermaidPending = false;
          this.pendingMermaidContent = '';
        } else {
          // 未完成的块不返回内容
          processedData = '';
        }
      } else {
        // 处理新的Mermaid块开始
        if (mermaidBlockMarker.start.test(newData)) {
          const [beforeMermaid, mermaidStart] = newData.split(mermaidBlockMarker.start);
          processedData = beforeMermaid || ''; // 返回非Mermaid部分

          // 开始缓存Mermaid内容
          this.isMermaidPending = true;
          this.pendingMermaidContent = mermaidStart ? `\`\`\`mermaid\n${mermaidStart}` : '```mermaid\n';
        } else {
          processedData = newData; // 非Mermaid内容直接返回
        }
      }
      return processedData;
    },

    // Mermaid 点击放大逻辑
    handleMermaidClick(graphDef, svgElement) {
      const oldPreview = document.querySelector('.mermaid-preview-container');
      if (oldPreview) document.body.removeChild(oldPreview);

      const previewContainer = document.createElement('div');
      previewContainer.className = 'mermaid-preview-container';
      previewContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0,0,0,0.41);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        padding: 20px;
        box-sizing: border-box;
      `;

      const clonedSvg = svgElement.cloneNode(true);
      clonedSvg.style.maxWidth = '90%';
      clonedSvg.style.maxHeight = '90%';
      clonedSvg.style.height = 'auto';

      const closeBtn = document.createElement('button');
      closeBtn.innerText = '关闭预览';
      closeBtn.style.cssText = `
        position: absolute;
        top: 20px;
        left: 20px;
        padding: 8px 16px;
        background: #fff;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        color: #333;
        font-size: 14px;
      `;

      previewContainer.appendChild(clonedSvg);
      previewContainer.appendChild(closeBtn);
      document.body.appendChild(previewContainer);

      const closePreview = () => {
        document.body.removeChild(previewContainer);
        closeBtn.removeEventListener('click', closePreview);
        previewContainer.removeEventListener('click', bgClickHandler);
      };

      const bgClickHandler = (e) => {
        if (e.target === previewContainer) closePreview();
      };

      closeBtn.addEventListener('click', closePreview);
      previewContainer.addEventListener('click', bgClickHandler);
    },

    // 处理$包裹的公式内容
    processDollarContent(content) {
      if (!content) return content;
      const regex = /(\$\$?)([\s\S]*?)(\1)/g;
      return content.replace(regex, (match, startDelimiter, formulaContent, endDelimiter) => {
        let processedContent = formulaContent.trim()
        return `${startDelimiter}${processedContent}${endDelimiter}`;
      });
    },

    // 编辑器事件处理（空实现，保留扩展能力）
    handleChange(value, render) { },
    handleSave(value, render) { },
    handleFullscreen(isFull) { },

    // 预览切换
    previewToggle(isPreview) {
      this.isEditMode = isPreview;
      let previewFlag = isPreview ? 'preview' : 'edit';
      this.$emit('preview', previewFlag)
      if (isPreview) this.renderMermaid()
    },

    subfieldToggle(value) { },

    // 设置编辑器背景色
    setMarkdownBodyBackground(color) {
      const editorDom = this.$refs.markdownEditor?.$el;
      if (!editorDom) return;

      const noteOp = editorDom.querySelector('.v-note-op');
      if (noteOp) {
        noteOp.style.background = color;
        noteOp.style.backgroundColor = color;
      }

      const markdownBody = editorDom.querySelector('.markdown-body');
      if (markdownBody) {
        const firstChild = markdownBody.querySelector(':scope > :first-child');
        if (firstChild) {
          firstChild.style.background = color;
        }
      }

      const editArea = editorDom.querySelector('.v-note-edit');
      if (editArea) {
        editArea.style.backgroundColor = color;
        const editInputWrapper = editArea.querySelector('.content-input-wrapper, .auto-textarea-wrapper');
        if (editInputWrapper) editInputWrapper.style.backgroundColor = color;
      }

      const textareaInput = editorDom.querySelector('.auto-textarea-wrapper .auto-textarea-input.no-resize');
      if (textareaInput) {
        textareaInput.style.backgroundColor = color;
        textareaInput.style.color = '#333';
      }

      const previewArea = editorDom.querySelector('.v-note-show .v-show-content');
      if (previewArea) {
        previewArea.style.backgroundColor = color;
      }
    },

    // 链接点击处理
    handleLinkClick(event) {
      const target = event.target;
      if (target.tagName.toLowerCase() === 'a') {
        const href = target.getAttribute('href');
        if (href && href.includes('id=')) {
          event.preventDefault();
          const id = this.extractIdFromHref(href);
          if (id) {
            this.$emit('lookDetail', id);
          }
        }
      }
    },
    extractIdFromHref(href) {
      const regex = /id=([^&]+)/;
      const match = href.match(regex);
      return match ? match[1] : null;
    },

    // 重置 mermaid 流式状态（供父组件调用）
    resetMermaidStreamStatus() {
      this.isMermaidPending = false;
      this.pendingMermaidContent = '';
    }
  },

  beforeDestroy() {
    const editorDom = this.$refs.markdownEditor?.$el;
    if (editorDom) {
      editorDom.removeEventListener('click', this.handleLinkClick);
    }
    // 清理预览容器
    const previewContainer = document.querySelector('.mermaid-preview-container');
    if (previewContainer) document.body.removeChild(previewContainer);
  },
  mounted() {
    this.$nextTick(() => {
      this.setMarkdownBodyBackground(this.defaultOpen === 'edit' ? '#f4f9ff70' : '#ffffff');
      const editorDom = this.$refs.markdownEditor?.$el;
      if (editorDom) {
        editorDom.addEventListener('click', this.handleLinkClick);
      }

      // 初始化 Mermaid
      this.initMermaid()
      // 优化渲染重试逻辑
      let retryCount = 0;
      const render = () => {
        this.renderMermaid();
        const hasMermaid = document.querySelector('svg[id^="mermaid-"]');
        if (!hasMermaid && retryCount < 3) {
          retryCount++;
          setTimeout(render, 300);
        }
      };
      render();
    });

  }
}
</script>

<style scoped>
@import 'katex/dist/katex.min.css';

::v-deep .markdown-body {
  font-size: 14px !important;
  line-height: 2 !important;
}

.markdown-editor {
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.katex {
  font-size: 1.1em;
  line-height: 2;
}

.katex .vlist {
  display: table-row-group;
  position: relative;
  vertical-align: bottom;
}

::v-deep .katex-display {
  margin: 1em 0;
  overflow-x: auto !important;
  overflow-y: clip !important;
  padding: 0.5em;
  min-height: 2em;
  height: auto !important;
  width: 100% !important;
  overflow-y: auto !important;
}

::v-deep .loading-append {
  color: #1890ff !important;
  font-weight: 500;
}

::v-deep .markdown-body .loading-append {
  display: inline;
  margin-left: 2px;
}

/* Mermaid 样式适配 + 强制隐藏所有错误内容 */
::v-deep svg {
  pointer-events: auto !important;
  max-width: 100% !important;
  height: auto !important;
}

::v-deep svg g,
::v-deep svg path,
::v-deep svg rect,
::v-deep svg text,
::v-deep svg polygon,
::v-deep svg line {
  pointer-events: none !important;
}

::v-deep .mermaid-container {
  position: relative !important;
  z-index: 10 !important;
  pointer-events: auto !important;
  width: 100% !important;
  cursor: pointer;
}

/* 强制隐藏所有 Mermaid 错误提示（覆盖所有场景） */
::v-deep .mermaid-error,
::v-deep .mermaid-syntax-error,
::v-deep .mermaid-parse-error,
::v-deep [id^="mermaid-error-"],
::v-deep [class*="error-text"],
::v-deep [class*="syntax-error"],
::v-deep pre:has(code.language-mermaid):not(:has(.mermaid-container)),
/* 包含错误文本的节点 */
::v-deep *:contains("Syntax error in graph"),
::v-deep *:contains("mermaid syntax error"),
::v-deep *:contains("解析失败") {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  width: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  z-index: -9999 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

/* 兼容不支持 :has 的浏览器 */
::v-deep pre code.language-mermaid+span,
::v-deep pre code.lang-mermaid+span,
::v-deep pre code.mermaid+span {
  display: none !important;
}

/* 兜底：隐藏所有可能的错误残留 */
::v-deep [data-processed="true"]+.mermaid-error,
::v-deep [data-processed="true"]+span,
::v-deep pre:has(code.language-mermaid):empty,
::v-deep .mermaid-container+.error-text {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  width: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  z-index: -9999 !important;
}

/* 强制隐藏所有Mermaid错误提示（覆盖所有可能的错误类名和文本） */
::v-deep .mermaid-error,
::v-deep .mermaid-syntax-error,
::v-deep [id^="mermaid-error-"],
::v-deep [class*="error-text"],
/* 包含错误文本的元素 */
::v-deep *:contains("Syntax error in graph"),
::v-deep *:contains("mermaid version 9.4.0"),
::v-deep *:contains("解析失败") {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  width: 0 !important;
  overflow: hidden !important;
  position: absolute !important;
  z-index: -9999 !important;
}

/* 兜底隐藏所有可能的错误提示 */
::v-deep * {

  &:contains("Syntax error in graph"),
  &:contains("mermaid version 9.4.0"),
  &:contains("解析失败") {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
  }
}

/* 兼容不支持:has的浏览器 */
::v-deep pre code.language-mermaid {

  &+span,
  &+div {
    display: none !important;
  }
}
</style>